import { TrendingUp, ShieldCheck, Users, FileCheck2 } from "lucide-react";

const benefits = [
  {
    icon: TrendingUp,
    title: "Sell More, Return Less",
    body: "Workshops re-order from brands that don't come back. The MWP1110's fail-safe design slashes warranty exposure across your entire pump category.",
    metric: "↑ Reorder rate",
  },
  {
    icon: ShieldCheck,
    title: "Proprietary Engineering",
    body: "Every critical failure point of the original and aftermarket designs has been engineered out at the geometry level — giving you a confidently differentiated SKU to stock.",
    metric: "Re-engineered from zero",
  },
  {
    icon: Users,
    title: "Happier End-Customers",
    body: "A driver whose car keeps cooling on a failed pump tells the story. A driver with a warped head writes a one-star review.",
    metric: "Story-worthy reliability",
  },
  {
    icon: FileCheck2,
    title: "One Part, Massive Coverage",
    body: "VW, Audi, Škoda, SEAT, Cupra — covering EA888 platforms across the most installed engine family in Europe. One SKU. Huge addressable demand.",
    metric: "VAG-wide fitment",
  },
];

export default function Distributor() {
  return (
    <section
      data-testid="distributor-section"
      className="relative py-28 lg:py-36 bg-[#F4F6FA] border-y border-[#C8D3DF] overflow-hidden"
    >
      <div className="absolute inset-0 dotted-bg opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left — copy */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 reveal">
            <span className="spec-label">For VAG Aftermarket Parts Distributors</span>
            <h2 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A2232] leading-[0.95] mt-5">
              Stock the VAG coolant pump<br />
              that <span className="text-[#5B9BD5]">sells itself.</span>
            </h2>
            <p className="mt-6 font-plex text-base text-[#536073] leading-relaxed">
              You're not selling a coolant pump. You're selling fewer warranty headaches, fewer comebacks, and a measurable bump in workshop loyalty. The MPS MWP1110 is the VAG aftermarket catalog upgrade your team didn't know to ask for.
            </p>

            <div className="mt-8 p-6 border border-[#5B9BD5]/40 bg-[#5B9BD5]/[0.04] rounded-sm">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#5B9BD5] uppercase mb-2">
                Distributor Snapshot
              </div>
              <div className="font-chivo font-bold text-[#1A2232] text-xl leading-snug">
                "If it ever fails, it fails open. The car keeps running. The customer never calls."
              </div>
              <div className="mt-3 font-mono text-[10px] tracking-[0.2em] text-[#8896A6] uppercase">
                — MPS Engineering Brief
              </div>
            </div>

            <a href="#cta" data-testid="distributor-cta" className="btn-tech mt-8">
              Request Distribution Terms <span aria-hidden>→</span>
            </a>
          </div>

          {/* Right — benefits */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  data-testid={`benefit-card-${i}`}
                  className="reveal group relative bg-white border border-[#C8D3DF] rounded-sm p-7 hover:border-[#5B9BD5]/50 transition-all duration-500"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-11 h-11 border border-[#C8D3DF] rounded-sm flex items-center justify-center group-hover:border-[#5B9BD5]/60 transition-colors">
                      <Icon size={18} className="text-[#5B9BD5]" />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-[#8896A6] uppercase">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-chivo font-bold text-xl text-[#1A2232] mb-3">{b.title}</h3>
                  <p className="font-plex text-sm text-[#536073] leading-relaxed">{b.body}</p>
                  <div className="mt-5 pt-4 border-t border-[#E8EEF6] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5B9BD5]" />
                    <span className="font-mono text-[10px] tracking-[0.25em] text-[#5B9BD5] uppercase">
                      {b.metric}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
